import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { format, addDays } from 'date-fns';
import {
  AlertCircle,
  ArrowLeft,
  Check,
  CheckCircle2,
  ChevronsUpDown,
  Download,
  RotateCcw,
  Send,
  SkipForward,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/app/components/ui/command';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/app/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { Textarea } from '@/app/components/ui/textarea';
import { toast } from '@/app/components/ui/simple-toaster';
import { cn } from '@/app/components/ui/utils';
import { useBatchDeploy, useResolveSerials } from '@/hooks/use-assets';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { useLocations, useOrganizationUsers } from '@/hooks/use-deployments';
import { useProject, useProjects } from '@/hooks/use-projects';
import type {
  BatchEntryMethod,
  Project,
  ReceiveGearItem,
  ResolvedSerialLine,
} from '@/lib/types';
import {
  BATCH_CHUNK_SIZE,
  downloadCsv,
  runInChunks,
  slugifyFilename,
  todayInputValue,
} from '@/app/pages/assets/batch-entry/batch-utils';
import type { BatchProgress, BatchSortKey } from '@/app/pages/assets/batch-entry/batch-utils';
import { BatchLineList } from '@/app/pages/assets/batch-entry/batch-line-list';
import type { BatchLine } from '@/app/pages/assets/batch-entry/batch-line-list';
import { buildSendGearCsv, deployOutcome, sendGearBlockReason } from './send-gear-utils';
import { SerialEntryPanel } from '@/app/pages/assets/batch-entry/serial-entry-panel';
import { useScanFeedback } from '@/app/pages/assets/batch-entry/use-scan-feedback';

const RESOLVE_CHUNK_SIZE = 200;

function defaultBatchName(): string {
  return `Send gear – ${todayInputValue()}`;
}

function errorMessage(error: unknown): string {
  if (isAxiosError<{ error?: string; detail?: string; project?: string[] }>(error)) {
    return (
      error.response?.data.error
      ?? error.response?.data.detail
      ?? error.response?.data.project?.[0]
      ?? error.message
    );
  }
  if (error instanceof Error) return error.message;
  return 'The request failed. These items were not confirmed as sent.';
}

export function SendGearPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedProjectId = Number(searchParams.get('project') ?? 0) || 0;
  const { data: preselectedProject } = useProject(preselectedProjectId);

  const resolveSerials = useResolveSerials();
  const batchDeploy = useBatchDeploy();
  const { data: locations } = useLocations();
  const { data: users } = useOrganizationUsers();
  const { feedbackClasses, playFeedback, soundEnabled, toggleSound } = useScanFeedback();

  const singleInputRef = useRef<HTMLInputElement>(null);
  const lineIdRef = useRef(0);

  const [batchId, setBatchId] = useState(() => crypto.randomUUID());
  const [batchName, setBatchName] = useState(defaultBatchName);
  const [manualProject, setManualProject] = useState<Project | null>(null);
  const [projectOpen, setProjectOpen] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');
  const debouncedProjectSearch = useDebouncedValue(projectSearch, 300);
  const { data: projectsData, isLoading: projectsLoading } = useProjects({
    search: debouncedProjectSearch,
    page_size: 50,
  });
  const [locationId, setLocationId] = useState<number | null>(null);
  const [assignedUserId, setAssignedUserId] = useState<number | null>(null);
  const [dueDate, setDueDate] = useState(() => format(addDays(new Date(), 7), 'yyyy-MM-dd'));
  const [notes, setNotes] = useState('');

  const [lines, setLines] = useState<BatchLine[]>([]);
  const [sortKey, setSortKey] = useState<BatchSortKey>('entered');
  const [groupByCategoryEnabled, setGroupByCategoryEnabled] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [progress, setProgress] = useState<BatchProgress>({ processed: 0, total: 0 });
  const [isFinished, setIsFinished] = useState(false);
  const [sendAttempted, setSendAttempted] = useState(false);
  const [retryAttempted, setRetryAttempted] = useState(false);

  const selectedProject = manualProject ?? preselectedProject ?? null;
  const projectReady = Boolean(selectedProject && batchName.trim());
  const projectsLocked = lines.some((line) => line.outcome?.status === 'deployed');

  useEffect(() => {
    if (projectReady && !isFinished) singleInputRef.current?.focus();
  }, [projectReady, isFinished]);

  const projectOptions = useMemo(
    () => (projectsData?.results ?? []).filter((project) => project.status !== 'cancelled'),
    [projectsData]
  );

  const resolveLines = useCallback(async (pendingLines: BatchLine[]): Promise<ResolvedSerialLine[]> => {
    const responses = await runInChunks(pendingLines, RESOLVE_CHUNK_SIZE, async (chunk) => {
      const response = await resolveSerials.mutateAsync({
        serials: chunk.map((line) => line.serial),
        purpose: 'deploy',
      });
      setLines((current) => current.map((line) => {
        const index = chunk.findIndex((pending) => pending.id === line.id);
        if (index === -1) return line;
        return { ...line, resolved: response.results[index] ?? line.resolved };
      }));
      return response;
    }, {
      onChunkError: (error, chunk) => {
        const message = errorMessage(error);
        setLines((current) => current.map((line) => (
          chunk.some((pending) => pending.id === line.id)
            ? {
                ...line,
                resolved: {
                  serial: line.serial,
                  status: 'not_found',
                  message: `Lookup failed: ${message}`,
                  warnings: [],
                  asset: null,
                },
              }
            : line
        )));
      },
    });
    return responses.flatMap((response) => response?.results ?? []);
  }, [resolveSerials]);

  const addSerials = useCallback(async (serials: string[], entryMethod: BatchEntryMethod) => {
    if (!projectReady) {
      toast.error('Pick the destination job before adding gear.');
      return [];
    }
    const existing = new Set(lines.map((line) => line.serial.toLocaleLowerCase()));
    const newLines: BatchLine[] = [];
    const duplicateLines: BatchLine[] = [];
    for (const raw of serials) {
      const serial = raw.trim();
      if (!serial) continue;
      const key = serial.toLocaleLowerCase();
      const line: BatchLine = {
        id: ++lineIdRef.current,
        serial,
        entryMethod,
        resolved: null,
      };
      if (existing.has(key)) {
        duplicateLines.push({
          ...line,
          resolved: {
            serial,
            status: 'duplicate',
            message: 'Already in this batch',
            warnings: [],
            asset: null,
          },
        });
        continue;
      }
      existing.add(key);
      newLines.push(line);
    }
    setIsFinished(false);
    setLines((current) => [...current, ...newLines, ...duplicateLines]);
    if (newLines.length === 0) {
      playFeedback('duplicate');
      return [];
    }
    return resolveLines(newLines);
  }, [lines, playFeedback, projectReady, resolveLines]);

  const handleSingle = useCallback(async (serial: string) => {
    const [resolved] = await addSerials([serial], 'single');
    // Feedback for a single scan comes from the resolved status.
    if (resolved) {
      playFeedback(
        resolved.status === 'ready'
          ? 'success'
          : resolved.status === 'duplicate'
            ? 'duplicate'
            : 'error'
      );
    }
    singleInputRef.current?.focus();
  }, [addSerials, playFeedback]);

  const handleBatch = useCallback(async (items: ReceiveGearItem[], method: 'paste' | 'range') => {
    await addSerials(items.map((item) => item.serial), method);
    const count = items.length;
    toast.success(`${count} serial${count === 1 ? '' : 's'} added — check the list before sending.`);
  }, [addSerials]);

  const removeLine = (line: BatchLine) => {
    setLines((current) => current.filter((candidate) => candidate.id !== line.id));
  };

  const pendingLines = lines.filter((line) => !line.outcome);
  const readyLines = pendingLines.filter((line) => line.resolved?.status === 'ready');
  const blockedLines = pendingLines.filter((line) => line.resolved && line.resolved.status !== 'ready');
  const resolvingCount = pendingLines.filter((line) => !line.resolved).length;
  const deployedLines = lines.filter((line) => line.outcome?.status === 'deployed');
  const skippedLines = lines.filter((line) => line.outcome?.status === 'skipped');
  const failedLines = lines.filter((line) => line.outcome?.status === 'failed');
  const sendBlockReason = sendGearBlockReason({
    hasProject: Boolean(selectedProject),
    batchName,
    readyCount: readyLines.length,
    resolvingCount,
  });
  // Retry runs the same checks as Send, over the failed lines.
  const retryBlockReason = sendGearBlockReason({
    hasProject: Boolean(selectedProject),
    batchName,
    readyCount: failedLines.length,
    resolvingCount,
  });
  // Shown only after a blocked click; they disappear once the cause is fixed.
  const sendError = sendAttempted ? sendBlockReason : null;
  const retryError = retryAttempted ? retryBlockReason : null;

  const handleSendClick = () => {
    if (sendBlockReason) {
      setSendAttempted(true);
      return;
    }
    setSendAttempted(false);
    void sendLines(pendingLines);
  };

  const sendLines = async (targetLines: BatchLine[]) => {
    if (!selectedProject || targetLines.length === 0) return;
    setIsSending(true);
    let deployed = 0;
    let skipped = 0;
    let failed = 0;

    await runInChunks(targetLines, BATCH_CHUNK_SIZE, async (chunk) => {
      const response = await batchDeploy.mutateAsync({
        batch_id: batchId,
        batch_name: batchName.trim(),
        entry_method: 'mixed',
        project: selectedProject.id,
        items: chunk.map((line) => ({ serial: line.serial })),
        deployed_to_location: locationId,
        assigned_to_user: assignedUserId,
        due_date: dueDate || null,
        notes: notes.trim(),
      });
      deployed += response.summary.deployed;
      skipped += response.summary.skipped;
      failed += response.summary.failed;
      setLines((current) => current.map((line) => {
        const index = chunk.findIndex((sent) => sent.id === line.id);
        if (index === -1) return line;
        const result = response.results[index];
        if (!result) {
          return {
            ...line,
            outcome: {
              status: 'failed',
              label: 'Failed',
              message: 'The server did not return a result for this serial.',
              tone: 'error',
            },
          };
        }
        return {
          ...line,
          resolved: line.resolved && result.asset
            ? { ...line.resolved, asset: result.asset }
            : line.resolved,
          outcome: deployOutcome(result),
        };
      }));
      return response;
    }, {
      onProgress: setProgress,
      onChunkError: (error, chunk) => {
        const message = errorMessage(error);
        failed += chunk.length;
        setLines((current) => current.map((line) => (
          chunk.some((sent) => sent.id === line.id)
            ? { ...line, outcome: { status: 'failed', label: 'Failed', message, tone: 'error' } }
            : line
        )));
      },
    });

    setIsSending(false);
    setIsFinished(true);
    if (failed > 0) {
      playFeedback('error');
      toast.error(`${deployed} sent · ${skipped} skipped · ${failed} failed (can be retried).`);
    } else if (skipped > 0) {
      playFeedback('duplicate');
      toast.warning(`${deployed} sent to ${selectedProject.name}; ${skipped} skipped — see the list.`);
    } else {
      playFeedback('success');
      toast.success(`${deployed} item${deployed === 1 ? '' : 's'} sent to ${selectedProject.name}.`);
    }
  };

  const retryFailed = () => {
    if (retryBlockReason) {
      setRetryAttempted(true);
      return;
    }
    setRetryAttempted(false);
    const retryLines = failedLines.map((line) => ({ ...line, outcome: undefined }));
    setLines((current) => current.map((line) => (
      line.outcome?.status === 'failed' ? { ...line, outcome: undefined } : line
    )));
    void sendLines(retryLines);
  };

  const downloadSummary = () => {
    downloadCsv(
      `${slugifyFilename(batchName, 'send-gear')}-summary.csv`,
      buildSendGearCsv(batchId, batchName, selectedProject?.name ?? '', lines)
    );
  };

  const startNewBatch = () => {
    setBatchId(crypto.randomUUID());
    setBatchName(defaultBatchName());
    setNotes('');
    setLines([]);
    setProgress({ processed: 0, total: 0 });
    setIsFinished(false);
    setSendAttempted(false);
    setRetryAttempted(false);
    window.setTimeout(() => singleInputRef.current?.focus());
  };

  return (
    <div id="send-gear-fields" className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-7xl mx-auto">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} aria-label="Back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Send gear</h1>
            <p className="text-gray-600 mt-1">
              Pick the job, then scan, paste, or generate serials. Each scan records a passed inspection by you.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/deployments/check-out">Single check out</Link>
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={toggleSound}
            aria-label={soundEnabled ? 'Mute scan sounds' : 'Enable scan sounds'}
          >
            {soundEnabled ? <Volume2 className="h-4 w-4 mr-2" /> : <VolumeX className="h-4 w-4 mr-2" />}
            {soundEnabled ? 'Sound on' : 'Sound off'}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>1. Destination job</CardTitle>
          <CardDescription>
            Every item in this batch is deployed to this job and shows on its work package.
            {projectsLocked ? ' Start a new batch to change the job.' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <fieldset disabled={projectsLocked || isSending} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div
                className="space-y-2"
                data-required="true"
                data-required-empty={selectedProject ? 'false' : 'true'}
                data-required-message="Pick the destination job."
              >
                <Label>Job / project *</Label>
                <Popover open={projectOpen} onOpenChange={setProjectOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={projectOpen}
                      className={cn('w-full justify-between', !selectedProject && 'text-muted-foreground')}
                      data-testid="send-gear-project"
                    >
                      {selectedProject
                        ? `${selectedProject.project_number} — ${selectedProject.name}`
                        : 'Search for a job…'}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder="Search by name or number…"
                        value={projectSearch}
                        onValueChange={setProjectSearch}
                      />
                      <CommandList>
                        <CommandEmpty>{projectsLoading ? 'Loading…' : 'No jobs found.'}</CommandEmpty>
                        <CommandGroup>
                          {projectOptions.map((project) => (
                            <CommandItem
                              key={project.id}
                              value={`${project.project_number} ${project.name}`}
                              onSelect={() => {
                                setManualProject(project);
                                setProjectOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  selectedProject?.id === project.id ? 'opacity-100' : 'opacity-0'
                                )}
                              />
                              <div className="flex flex-col">
                                <span className="font-medium">{project.name}</span>
                                <span className="text-sm text-gray-500">
                                  {project.project_number}
                                  {project.client_name ? ` · ${project.client_name}` : ''}
                                  {project.status ? ` · ${project.status.replace('_', ' ')}` : ''}
                                </span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label htmlFor="batch-name">Batch name *</Label>
                <Input id="batch-name" value={batchName} onChange={(event) => setBatchName(event.target.value)} required />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="deploy-location">Site location (optional)</Label>
                <Select
                  value={locationId ? String(locationId) : '__none__'}
                  onValueChange={(value) => setLocationId(value === '__none__' ? null : Number(value))}
                >
                  <SelectTrigger id="deploy-location">
                    <SelectValue placeholder="Select a location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No location</SelectItem>
                    {locations?.map((location) => (
                      <SelectItem key={location.id} value={String(location.id)}>
                        {location.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="assign-user">Assign to (optional)</Label>
                <Select
                  value={assignedUserId ? String(assignedUserId) : '__none__'}
                  onValueChange={(value) => setAssignedUserId(value === '__none__' ? null : Number(value))}
                >
                  <SelectTrigger id="assign-user">
                    <SelectValue placeholder="Select a user" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nobody</SelectItem>
                    {users?.map((user) => (
                      <SelectItem key={user.id} value={String(user.id)}>
                        {user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="due-date">Expected return</Label>
                <Input id="due-date" type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="deploy-notes">Notes (optional)</Label>
              <Textarea id="deploy-notes" rows={2} value={notes} onChange={(event) => setNotes(event.target.value)} />
            </div>
          </fieldset>
        </CardContent>
      </Card>

      {!isFinished && (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(360px,1.2fr)]">
          <Card>
            <CardHeader>
              <CardTitle>2. Add gear</CardTitle>
              <CardDescription>
                Scan with a USB/Bluetooth scanner, type and press Enter, paste a list, or enter a serial range.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SerialEntryPanel
                key={batchId}
                singleInputRef={singleInputRef}
                ready={projectReady}
                disabledPlaceholder="Pick the destination job first"
                processing={isSending}
                progress={progress}
                progressLabel="Sending"
                actionVerb="Add"
                rangeSummarySuffix="will be added to the list"
                validationScope="send-gear-fields"
                onSingle={(serial) => void handleSingle(serial)}
                onBatch={handleBatch}
              />
            </CardContent>
          </Card>

          <Card className={`transition-colors ${feedbackClasses}`} aria-live="polite">
            <CardHeader>
              <CardTitle>{readyLines.length.toLocaleString()} ready to send</CardTitle>
              <CardDescription>
                {blockedLines.length} can't go · {resolvingCount > 0 ? `${resolvingCount} looking up · ` : ''}
                {deployedLines.length} already sent in this batch
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {blockedLines.length > 0 && (
                <div role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                  {blockedLines.length} line{blockedLines.length === 1 ? '' : 's'} will be skipped and listed in the
                  summary (not found, already deployed, retired, quarantined, out for repair…). The rest still go.
                </div>
              )}
              <BatchLineList
                lines={pendingLines}
                sortKey={sortKey}
                onSortKeyChange={setSortKey}
                groupByCategoryEnabled={groupByCategoryEnabled}
                onGroupByCategoryChange={setGroupByCategoryEnabled}
                onRemove={removeLine}
                disabled={isSending}
              />
              {pendingLines.length > 0 && (
                <div className="space-y-2">
                  <Button
                    type="button"
                    className="w-full"
                    data-skip-required-validation="true"
                    aria-describedby={sendError ? 'send-gear-error' : undefined}
                    disabled={isSending}
                    onClick={handleSendClick}
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {isSending
                      ? 'Sending…'
                      : readyLines.length === 0
                        ? 'Send gear'
                        : `Send ${readyLines.length.toLocaleString()} item${readyLines.length === 1 ? '' : 's'} to ${selectedProject?.name ?? 'job'}`}
                  </Button>
                  {sendError && (
                    <p id="send-gear-error" role="alert" className="text-sm font-medium text-red-600">
                      {sendError}
                    </p>
                  )}
                </div>
              )}
              {deployedLines.length > 0 && pendingLines.length === 0 && (
                <Button type="button" variant="outline" className="w-full" onClick={() => setIsFinished(true)}>
                  View batch summary
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {isFinished && (
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle>3. Batch summary</CardTitle>
                <CardDescription>
                  {batchName} → {selectedProject?.name}
                </CardDescription>
              </div>
              <Button type="button" variant="outline" onClick={downloadSummary}>
                <Download className="h-4 w-4 mr-2" />
                Download CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                <CheckCircle2 className="h-5 w-5 text-green-700 mb-2" />
                <p className="text-2xl font-bold text-green-900">{deployedLines.length}</p>
                <p className="text-sm text-green-700">Sent (inspection recorded)</p>
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <SkipForward className="h-5 w-5 text-amber-700 mb-2" />
                <p className="text-2xl font-bold text-amber-900">{skippedLines.length}</p>
                <p className="text-sm text-amber-700">Skipped (not found / blocked)</p>
              </div>
              <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                <AlertCircle className="h-5 w-5 text-red-700 mb-2" />
                <p className="text-2xl font-bold text-red-900">{failedLines.length}</p>
                <p className="text-sm text-red-700">Failed</p>
              </div>
            </div>

            {failedLines.length > 0 && (
              <div role="alert" className="space-y-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900">
                <div className="flex items-center justify-between gap-3">
                  <span>{failedLines.length} item{failedLines.length === 1 ? '' : 's'} failed to send.</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    aria-describedby={retryError ? 'send-gear-retry-error' : undefined}
                    onClick={retryFailed}
                    disabled={isSending}
                  >
                    <RotateCcw className="h-4 w-4 mr-1" />
                    Retry failed
                  </Button>
                </div>
                {retryError && (
                  <p id="send-gear-retry-error" className="font-medium text-red-600">
                    {retryError}
                  </p>
                )}
              </div>
            )}

            <BatchLineList
              lines={lines}
              sortKey={sortKey}
              onSortKeyChange={setSortKey}
              groupByCategoryEnabled={groupByCategoryEnabled}
              onGroupByCategoryChange={setGroupByCategoryEnabled}
              maxHeightClass="max-h-[40rem]"
            />

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
              <div className="flex gap-2">
                {selectedProject && (
                  <Button type="button" variant="outline" asChild>
                    <Link to={`/projects/${selectedProject.id}`}>Open job</Link>
                  </Button>
                )}
                <Button type="button" variant="outline" asChild>
                  <Link to="/deployments">Deployments</Link>
                </Button>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setIsFinished(false)}>
                  Send more to this job
                </Button>
                <Button type="button" onClick={startNewBatch}>Start new batch</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
